import { Localized } from '../../i18n/I18n';
import React, { useEffect, useRef, useState } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

const scenes = [
  { id: 'connected-devices', label: 'IA e integraciones', title: 'Tecnología que conecta tu negocio.', description: 'Integramos tus herramientas y automatizamos procesos con IA. Tu equipo gana tiempo para lo que importa.', link: '#en-accion', action: 'Ver una automatización' },
  { id: 'digital-systems', label: 'Desarrollo digital', title: 'De tu idea a una solución real.', description: 'Creamos sitios, aplicaciones y sistemas a medida. Una experiencia clara, desde el primer clic.', link: '/servicios', action: 'Explorar los servicios' },
  { id: 'human-technology', label: 'Tecnología aplicada', title: 'El siguiente paso empieza contigo.', description: 'Desarrollo, infraestructura y acompañamiento para convertir la tecnología en parte de tu operación.', link: '#metodo', action: 'Cómo trabajamos' },
  { id: 'computer-support', label: 'Soporte de cómputo', title: 'Tu equipo, listo para seguir.', description: 'Diagnóstico, mantenimiento y soporte para los equipos que sostienen tu trabajo. Atención clara, de principio a fin.', link: '/servicios/soporte-computo', action: 'Conocer el soporte' },
];

export default function VideoHero() {
  const section = useRef(null);
  const videos = useRef([]);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [visible, setVisible] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(playing);
  const [focused, setFocused] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [failed, setFailed] = useState({});
  const [announcement, setAnnouncement] = useState('');
  const canPlay = playing && visible && pageVisible;

  useEffect(() => {
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => setVisible(entries[0].isIntersecting), { threshold: 0.15 }) : null;
    if (observer && section.current) observer.observe(section.current);
    return () => {
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    videos.current.forEach((video, index) => {
      if (!video) return;
      if (index === active && canPlay && !failed[index]) {
        const request = video.play();
        // A browser may reject autoplay. The visible play control remains usable.
        request?.catch(() => {});
      } else video.pause();
    });
  }, [active, canPlay, failed]);

  useEffect(() => {
    if (!canPlay || focused) return undefined;
    const timer = window.setTimeout(() => setActive(index => (index + 1) % scenes.length), 8000);
    return () => window.clearTimeout(timer);
  }, [active, canPlay, focused]);

  const choose = index => {
    setActive(index);
    setAnnouncement(`Escena ${index + 1} de ${scenes.length}: ${scenes[index].label}`);
  };
  const togglePlayback = () => {
    setVideoEnabled(true);
    // Explicit play resumes rotation even if the play control retains focus.
    if (!playing) setFocused(false);
    setPlaying(value => !value);
  };
  const move = direction => choose((active + direction + scenes.length) % scenes.length);
  const scene = scenes[active];

  return <Localized><section id="hero" ref={section} className="bz-video-hero" aria-roledescription="carrusel" aria-label="Tecnología y servicios Bezalel">
    <div className="bz-video-background" aria-hidden="true">
      {scenes.map((item, index) => <div key={item.id} className={`bz-video-scene ${index === active ? 'is-active' : ''}`} style={{ backgroundImage: `url(/assets/video/${item.id}.jpg)` }}>
        <video ref={element => { videos.current[index] = element; }} src={index === active && videoEnabled && !failed[index] ? `/assets/video/${item.id}.mp4` : undefined} poster={`/assets/video/${item.id}.jpg`} autoPlay={index === active && canPlay} muted loop playsInline preload={index === active ? 'auto' : 'none'} tabIndex={-1} onError={() => setFailed(value => ({ ...value, [index]: true }))}/>
      </div>)}
    </div>
    <div className="bz-video-wash" aria-hidden="true"/>
    <div className="bz-shell bz-video-content">
      <div className="bz-video-copy" key={scene.id}>
        <p className="bz-video-category">{scene.label}</p>
        <h1>{scene.title}</h1>
        <p className="bz-video-description">{scene.description}</p>
        <div className="bz-video-actions"><a className="bz-button" href="#contacto">Hablemos de tu proyecto <ArrowUpRight size={19}/></a><a className="bz-text-link" href={scene.link}>{scene.action}<ArrowUpRight size={18}/></a></div>
      </div>
    </div>
    <div className="bz-shell bz-video-toolbar" onFocusCapture={() => setFocused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <div className="bz-video-select" aria-label="Elegir escena">{scenes.map((item, index) => <button type="button" key={item.id} aria-label={`Ver escena ${index + 1}: ${item.label}`} aria-current={active === index ? 'true' : undefined} onClick={() => choose(index)}><span className="bz-video-tab-line"/><span className="bz-video-tab-number">0{index + 1}</span><span>{item.label}</span></button>)}</div>
      <div className="bz-video-controls"><button type="button" onClick={() => move(-1)} aria-label="Escena anterior"><ChevronLeft size={22} strokeWidth={1.4} aria-hidden="true"/></button><button type="button" onClick={togglePlayback} aria-label={playing ? 'Pausar videos y transiciones' : 'Reproducir videos y transiciones'}>{playing ? <Pause size={22} strokeWidth={1.4} aria-hidden="true"/> : <Play size={22} strokeWidth={1.4} aria-hidden="true"/>}</button><button type="button" onClick={() => move(1)} aria-label="Siguiente escena"><ChevronRight size={22} strokeWidth={1.4} aria-hidden="true"/></button></div>
    </div>
    <span className="bz-visually-hidden" role="status" aria-live="polite">{announcement}</span>
  </section></Localized>;
}
